/**
 * Handoff-rule editors (escalation-admin-UI spec 2026-07-10 §3.5). All
 * inputs write through the workflowConfigStore's pruning escalation
 * setters — the overlay stays sparse. Deliberately NOT Advanced-gated
 * (spec decision 7): disabling rules and retuning stall are the primary
 * operator actions on this tab. Disable = the semantic {enabled:false}
 * patch, never the keyed-section tombstone.
 */
import { type ChangeEvent } from 'react'
import { TagInput } from '@/components/ui/TagInput'
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'
import { InlineFindings } from './entryControls'
import { BilingualTextEditor } from './editors'
import { extractTokens } from '@/utils/friendlyLabels'
import type { ConfigHandoffRuleVM } from '@/utils/workflowConfigView'
import type { OrderWorkflowDefinitionJson } from '@/types/workflowDefinition'

const inputCls =
  'rounded-sm border border-border bg-background p-1 text-xs text-foreground'
const btnCls =
  'rounded-sm border border-border px-2 py-0.5 text-xs font-medium text-foreground hover:bg-muted'

/**
 * Change handler for the positive-integer trigger knobs. Clearing a
 * controlled number input fires `Number('') === 0`, which would write
 * e.g. {after_turns: 0} ("escalate after 0 turns", below the schema's
 * ge=1 minimum) into the live draft overlay — so empty/invalid/
 * sub-minimum values commit NOTHING and the controlled input snaps
 * back to the effective value. Resetting to base = typing the base
 * value, which prunes.
 */
function commitPositiveInt(commit: (n: number) => void) {
  return (e: ChangeEvent<HTMLInputElement>) => {
    const n = Number(e.target.value)
    if (e.target.value === '' || !Number.isInteger(n) || n < 1) return
    commit(n)
  }
}

export function HandoffRuleControls({ rule }: { rule: ConfigHandoffRuleVM }) {
  const setTriggerField = useWorkflowConfigStore((s) => s.setTriggerField)
  const removeAddedTrigger = useWorkflowConfigStore((s) => s.removeAddedTrigger)
  if (!rule.id) return null
  if (rule.origin === 'added') {
    return (
      <button type="button" className={btnCls} aria-label={`remove ${rule.id}`}
        onClick={() => removeAddedTrigger(rule.id!)}>
        Remove
      </button>
    )
  }
  return rule.disabled ? (
    <button type="button" className={btnCls} aria-label={`re-enable ${rule.id}`}
      onClick={() => setTriggerField(rule.id!, 'enabled', true)}>
      Re-enable
    </button>
  ) : (
    <button type="button" className={btnCls} aria-label={`disable ${rule.id}`}
      onClick={() => setTriggerField(rule.id!, 'enabled', false)}>
      Disable
    </button>
  )
}

/**
 * Chip-based phrase editor (SA-125): phrases are atomic — the parameters
 * card's TagInput authors comma-containing phrases into these same
 * `match` leaves, so free-text comma parsing here would silently split
 * them on save. The call site keys this component by rule id + lang so
 * TagInput's draft buffer resets when switching rules.
 */
function KeywordsInput({ ruleId, lang, phrases }: {
  ruleId: string
  lang: string
  phrases: string[]
}) {
  const setTriggerKeywords = useWorkflowConfigStore((s) => s.setTriggerKeywords)
  return (
    <div className="max-w-md text-xs text-foreground">
      <span className="font-mono uppercase text-muted-foreground">{lang}</span>{' '}
      phrases
      <TagInput
        value={phrases}
        onChange={(list) => setTriggerKeywords(ruleId, lang, list)}
        normalize={(s) => s.trim()}
        maxTags={50}
        ariaLabel={`phrases in ${lang} for ${ruleId}`}
        placeholder="Add a phrase and press Enter"
        className="mt-1"
      />
    </div>
  )
}

export function HandoffRuleEditor({ rule, languages }: {
  rule: ConfigHandoffRuleVM
  languages: string[]
}) {
  const setTriggerField = useWorkflowConfigStore((s) => s.setTriggerField)
  if (!rule.id) return null
  const id = rule.id
  const keywordLangs = Array.from(
    new Set([...languages, ...Object.keys(rule.keywords ?? {})]),
  )
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-xs text-foreground">
        Response
        <select
          aria-label={`response for ${id}`}
          className={inputCls}
          value={rule.mode}
          onChange={(e) => setTriggerField(id, 'mode', e.target.value)}
        >
          <option value="hard">Hands off immediately</option>
          <option value="advisory">Suggests offering a handoff</option>
        </select>
      </label>
      {rule.when === 'stalled' && (
        <div className="flex flex-wrap items-center gap-4 text-xs text-foreground">
          <label className="flex items-center gap-2">
            Turns without progress
            <input
              aria-label={`turns without progress for ${id}`}
              type="number" min={1} className={`${inputCls} w-20`}
              value={rule.afterTurns ?? ''}
              onChange={commitPositiveInt((n) => setTriggerField(id, 'after_turns', n))}
            />
          </label>
          <label className="flex items-center gap-2">
            Quiet minutes before the count resets
            <input
              aria-label={`quiet minutes before the count resets for ${id}`}
              type="number" min={1} className={`${inputCls} w-20`}
              value={rule.resetAfterMinutes ?? ''}
              onChange={commitPositiveInt((n) => setTriggerField(id, 'reset_after_minutes', n))}
            />
          </label>
        </div>
      )}
      {rule.when === 'unknown_slots_seen' && (
        <label className="flex items-center gap-2 text-xs text-foreground">
          Occurrences before handoff
          <input
            aria-label={`occurrences before handoff for ${id}`}
            type="number" min={1} className={`${inputCls} w-20`}
            value={rule.after}
            onChange={commitPositiveInt((n) => setTriggerField(id, 'after', n))}
          />
        </label>
      )}
      {rule.when === 'slot_above' && (
        <label className="flex items-center gap-2 text-xs text-foreground">
          More than
          <input
            aria-label={`limit for ${id}`}
            type="number" min={1} className={`${inputCls} w-20`}
            value={rule.above ?? ''}
            onChange={commitPositiveInt((n) => setTriggerField(id, 'above', n))}
          />
        </label>
      )}
      {rule.when === 'topic_denylist' && (
        <div className="space-y-1">
          {keywordLangs.map((lang) => (
            <KeywordsInput
              key={`${id}:${lang}`}
              ruleId={id}
              lang={lang}
              phrases={rule.keywords?.[lang] ?? []}
            />
          ))}
        </div>
      )}
      <InlineFindings paths={[`escalation.triggers.${id}`]} />
    </div>
  )
}

export function HandoffMessageEditor({ msgKey, base }: {
  msgKey: string
  base: OrderWorkflowDefinitionJson
}) {
  const store = useWorkflowConfigStore()
  const baseText = base.escalation?.messages?.[msgKey] ?? {}
  const effective = {
    ...baseText,
    ...(store.overlay.escalation?.messages?.[msgKey] ?? {}),
  }
  return (
    <div className="space-y-1">
      <BilingualTextEditor
        value={effective}
        baseValue={baseText}
        onChange={(lang, text) => store.setEscalationMessage(msgKey, lang, text)}
        tokenLegend={extractTokens(baseText)}
      />
      <InlineFindings paths={[`escalation.messages.${msgKey}`]} />
    </div>
  )
}
